import { PencilSimpleIcon } from '@c/icons';
import Accordion from '@ui/Accordion';
import Button from '@ui/Button';
import RadioButton from '@ui/RadioButton';
import { useAuth } from 'context/AuthContext';
import { UserSizes } from 'models/user';
import Link from 'next/link';
import React from 'react';

interface Size {
  key: keyof UserSizes | 'gear combos';
  value: string;
}

const InYourSizeFilterSection = ({
  selectedSizes,
  handleSizeChange,
}: {
  selectedSizes?: Size;
  handleSizeChange: (sizes: Size) => void;
}) => {
  const { userDoc } = useAuth();
  const userSizes = userDoc?.sizes;
  const toggleSize = (size: Size) => {
    handleSizeChange(size);
  };

  return (
    <div className="rounded-2xl border-2 border-brand-secondary bg-brand-secondary-light">
      <Accordion title={'Shop by Size'} startOpen={true}>
        <div className="flex flex-col gap-[1.6rem] p-4 ">
          {Object.entries(userSizes ?? {})?.map((size) => (
            <SizeSelector
              size={{
                key: size[0] as Size['key'],
                value: size[1] as string,
              }}
              key={size[0]}
              onToggle={() =>
                toggleSize({
                  key: size[0] as Size['key'],
                  value: size[1] as string,
                })
              }
              selected={
                !!selectedSizes &&
                selectedSizes.key?.includes(size[0]) &&
                selectedSizes.value === `${size[1]}`
              }
            />
          ))}

          {userSizes?.jersey && userSizes.pants && (
            <SizeSelector
              size={{
                key: 'gear combos',
                value: `${userSizes.jersey}/${userSizes.pants}`,
              }}
              key={'gear-combos'}
              onToggle={() =>
                toggleSize({
                  key: 'gear combos',
                  value: `${userSizes.jersey}/${userSizes.pants}`,
                })
              }
              selected={
                !!selectedSizes &&
                selectedSizes.key?.includes('gear combos') &&
                selectedSizes.value === `${userSizes.jersey}/${userSizes.pants}`
              }
            />
          )}

          {userDoc ? (
            <Button
              leadingIcon={<PencilSimpleIcon />}
              text="Update Sizes"
              height="small"
              href="/dashboard/my-profile"
              type="secondary"
            />
          ) : (
            <Button
              text="Sign up to add sizes"
              height="small"
              href="/signup"
              type="secondary"
            />
          )}

          <span className="text-[1.2rem] text-brand-primary">
            Looking for other sizes? Use the filters down below.
          </span>
        </div>
      </Accordion>
    </div>
  );
};

const SizeSelector = ({
  size,
  selected,
  onToggle,
}: {
  size: Size;
  selected: boolean;
  onToggle: () => void;
}) => (
  <div className="flex items-center justify-between">
    <span className="font-semibold">
      {capitalizeLabel(size.key)}: ({size.value})
    </span>
    <RadioButton onClick={onToggle} checked={selected} />
    <Link
      hidden
      href={
        size.key === 'gloves'
          ? `/shop/riding-gear/gear/size-${size.value}-gloves`
          : size.key === 'boots'
          ? `/shop/riding-gear/size-${size.value}-boots`
          : size.key === 'pants'
          ? `/shop/riding-gear/gear/size-${size.value}-pants`
          : size.key === 'jersey'
          ? `/shop/riding-gear/gear/size-${size.value}-jerseys`
          : size.key === 'helmet'
          ? `/shop/riding-gear/size-${size.value}-helmets`
          : ''
      }
    />
  </div>
);

function capitalizeLabel(label: string) {
  const words = label.split(' ');
  return words.map((word) => word[0].toUpperCase() + word.slice(1)).join(' ');
}

export default InYourSizeFilterSection;
